import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { Button, Card, CardHeader, CardBody, Container, Col, Row, Label, FormGroup, FormFeedback, InputGroup, InputGroupText, Form, Input, } from "reactstrap";
import classnames from "classnames";
import { Link, useNavigate, useParams } from "react-router-dom";  // ✅ FIX

import SimpleHeader from "@/components/Headers/SimpleHeader";
import React, { useEffect, useState } from "react";


import {
  getSiteCategories,
  patchCategory,
} from "@/services/categoryServices/categoryAction";

import { getSites } from "@/services/siteServices/siteActions";
import {
  findOverlayRow,
  getTaxonomyOverlays,
} from "@/services/taxonomyOverlayServices/taxonomyOverlayApi";
import type { TaxonomyOverlayRow } from "@/services/taxonomyOverlayServices/taxonomyOverlayApi";

/**
 * SCRUM-1704: the slug field's error text for a refusal of PATCH /api/category/patch.
 * A 409 names the row that holds the slug — by its label when the site's rows are
 * loaded, by its id otherwise — so the editor knows which node to look at.
 *
 * `error` is the response body (or `{ message }` on a transport failure).
 */
export function describeRefusal(error: any, rows: TaxonomyOverlayRow[] | null): string {
  if (error && error.error === "DUPLICATE_OVERLAY_SLUG") {
    const holder = (rows || []).find((row) => row && String(row._id) === String(error.existingId));
    const who = holder
      ? `"${holder.label}" (${holder.nodeKind}, row ${error.existingId})`
      : error.existingId
        ? `row ${error.existingId}`
        : "another node";
    return `This slug is already used on this site by ${who}. Choose another path.`;
  }
  if (error && error.error === "OVERLAY_SLUG_INVALID") {
    return error.msg || "This slug cannot be used as the site path.";
  }
  return error?.msg || error?.message || "The label could not be saved.";
}

const LabelCategories = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const siteCategories_redux = useAppSelector(
    (state) => state.categories.siteCategories
  ) as any;
  const currentSite_redux = useAppSelector((state) => state.sites.site);
  const sites = useAppSelector((state) => state.sites.sites);
  const navigate = useNavigate();

  const [categoryToLabel, setCategoryToLabel] = useState<any>({}); // any: an entry of categories.siteCategories, typed any[] there (categorySlice.ts:39)
  // SCRUM-1851: this site's label and slug for the category — its TaxonomyOverlay
  // row and nothing else. `null` = the site has no row (the "not labelled"
  // screen); an object = the inputs' values. The node's stored `label[]` is not
  // read, edited or posted any more (it leaves the schema with SCRUM-1716).
  const [entry, setEntry] = useState<{ label: string, slug: string } | null>(
    null
  );
  // SCRUM-1704: the site's TaxonomyOverlay rows (SCRUM-1703) — null until the
  // read answers, so the uncontrolled inputs below mount once, with the right
  // defaults.
  const [siteOverlays, setSiteOverlays] = useState<TaxonomyOverlayRow[] | null>(
    null
  );
  // The slug field's own error: a 400 / 409 from PATCH /api/category/patch.
  const [slugError, setSlugError] = useState("");

  useEffect(() => {
    const siteId = currentSite_redux?._id;
    if (!siteId) return undefined;
    let cancelled = false;
    getTaxonomyOverlays(siteId)
      .then((rows) => {
        if (!cancelled) setSiteOverlays(rows);
      })
      .catch(() => {
        // Unreadable rows are "no rows": the site reads as not labelled.
        if (!cancelled) setSiteOverlays([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentSite_redux?._id]);

  useEffect(() => {
    dispatch(getSiteCategories());
    if (siteCategories_redux.length > 0 && siteOverlays !== null) {
      const found = siteCategories_redux.find((cat) => cat._id === id) || {};
      const row = findOverlayRow(siteOverlays, "category", id);
      setCategoryToLabel(found);
      setEntry(row ? { label: row.label ?? "", slug: row.slug ?? "" } : null);
    }

    if (sites.length === 0) {
      dispatch(getSites());
    }
  }, [JSON.stringify(siteCategories_redux), sites.length, id, dispatch, siteOverlays]);

  // SCRUM-1393 kept this handler from throwing when the site had no entry yet;
  // with the entry in its own state there is nothing to look up.
  const changeParams = (e) => {
    const { name, value } = e.target;
    setEntry((prev) => ({ ...(prev || { label: "", slug: "" }), [name]: value }));
  };

  // "Add Label": open the form with empty fields. Nothing is written until Update.
  const onLink = (e) => {
    e.preventDefault();
    setEntry({ label: "", slug: "" });
  };

  // Dispatch call to the DB
  //
  // SCRUM-1704: wait for the answer. The route refuses a slug it cannot turn into
  // an overlay row (400 OVERLAY_SLUG_INVALID) or one another node on this site
  // already holds (409 DUPLICATE_OVERLAY_SLUG) before it writes anything; the
  // refusal lands on the slug field, the navigation is the success path only.
  //
  // SCRUM-1851: the body carries this site's entry and nothing else — the route
  // reads the caller's own entry into the row (SCRUM-1703), and the other
  // tenants' stored `label[]` entries are no business of this screen.
  const updateLabelCategory = async () => {
    setSlugError("");
    const result = await dispatch(
      patchCategory({
        _id: categoryToLabel._id,
        label: [
          {
            siteid: currentSite_redux._id,
            value: { label: entry?.label ?? "", slug: entry?.slug ?? "" },
          },
        ],
      })
    );
    if (result && result.ok === false) {
      setSlugError(describeRefusal(result.error, siteOverlays));
      return;
    }
    navigate("/admin/category/site");
  };

  const currentSlug = (entry?.slug || "").trim();

  if (!categoryToLabel._id || sites.length === 0 || !currentSite_redux._id) {
    return <></>;
  }

  if (entry === null) {
    return (
      <>
        <SimpleHeader name="Label Category" parentName="Categories" />
        <Container className="mt--6" fluid>
          <Card>
            <CardHeader>{categoryToLabel.name} is not Labeled</CardHeader>
            <CardBody>
              <Button className="my-0" color="btn btn-outline-success" onClick={onLink}>
                Add Label for {categoryToLabel.name}
              </Button>
              <FormGroup className="row">
                <Col md="12" className=" text-right">
                  <Button
                    className="my-0"
                    color="btn btn-outline-default"
                    to="/admin/category/site"
                    tag={Link}
                  >
                    Back To Site Categories
                  </Button>
                </Col>
              </FormGroup>
            </CardBody>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <SimpleHeader name="Label Category" parentName="Categories" />
      <Container className="mt--6" fluid>
        <Card>
          <CardHeader>
            <Row className="align-items-center">
              <Col xs="8">
                <h5 className="h3 mb-0">
                  {" "}
                  Assign a Label for <span className="text-primary">{categoryToLabel.name}</span>{" "}
                </h5>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <FormGroup className="row">
              <Label className="form-control-label" htmlFor="example-text-input" md="1">
                Label
              </Label>
              <Col md="9">
                <InputGroup className={classnames("input-group-merge")}>
                  <InputGroupText>
                    <i className="ni ni-box-2" />
                  </InputGroupText>
                  <Input
                    placeholder="Insert Category Label"
                    name="label"
                    type="text"
                    defaultValue={entry.label}
                    onChange={changeParams}
                  />
                </InputGroup>
              </Col>
            </FormGroup>

            <FormGroup className="row">
              <Label className="form-control-label" htmlFor="example-text-input" md="1">
                Slug
              </Label>
              <Col md="9">
                <InputGroup className={classnames("input-group-merge")}>
                  <InputGroupText>
                    <i className="ni ni-box-2" />
                  </InputGroupText>
                  <Input
                    placeholder="Insert Category Slug without any spaces"
                    name="slug"
                    type="text"
                    onChange={(e) => {
                      setSlugError("");
                      changeParams(e);
                    }}
                    defaultValue={entry.slug}
                    invalid={!!slugError}
                  />
                  {/* SCRUM-1704: the slug is the whole site-relative path
                      (`bankrekening/student`); the URL it yields is shown
                      next to it, and a refusal from the route lands here. */}
                  {!!slugError && (
                    <FormFeedback className="d-block" data-testid="slug-error">
                      {slugError}
                    </FormFeedback>
                  )}
                </InputGroup>
                <small className="form-text text-muted" data-testid="slug-url-preview">
                  URL: /{currentSlug}/
                </small>
              </Col>
            </FormGroup>

            <FormGroup className="row">
              <Col md="12" className=" text-right">
                <Button
                  className="my-0"
                  color="btn btn-outline-default"
                  to="/admin/category/site"
                  tag={Link}
                >
                  Back To Site Categories
                </Button>
                <Button
                  className="my-4"
                  color="btn btn-outline-primary"
                  type="submit"
                  onClick={updateLabelCategory}
                >
                  Update
                </Button>
              </Col>
            </FormGroup>
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default LabelCategories;
